/*
  La Sura - <lasura-basket-when>, "When do you need it?" in the basket
  (snippets/lasura-basket-when.liquid), and the Check out guard. Kept small: the working, which
  builds the days and checks postcodes, is assets/lasura-basket-when-card.js, imported when the
  card comes into view, is touched, or Check out is pressed (import on interaction).
*/
let card;
const load = () =>
  (card ||= import('@theme/lasura-basket-when-card').catch((error) => {
    card = null; // a later press may try again
    throw error;
  }));

class LasuraBasketWhen extends HTMLElement {
  static observedAttributes = ['data-methods', 'data-days', 'data-total', 'data-closed', 'data-closed-dates', 'data-full-dates', 'data-load', 'data-units', 'data-slot-load', 'data-saved-day', 'data-has-subscription', 'data-lines'];

  connectedCallback() {
    this.start = () => this.whenReady().catch(() => {});
    this.addEventListener('pointerenter', this.start, { once: true });
    this.addEventListener('focusin', this.start, { once: true });
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.start();
      });
      this.io.observe(this);
    } else this.start();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.controller?.destroy();
    this.controller = null;
  }

  attributeChangedCallback(name, before, value) {
    // A day saved elsewhere (the product page's "When's it for?", blocks/lasura-when-check.liquid)
    // arrives with the basket's next re-render; take it, and render() checks it still works.
    if (name === 'data-saved-day' && value && this.controller && this.controller.state.iso !== value) {
      this.controller.state.iso = value;
    }
    this.controller?.render();
  }

  /** The card's working, loaded once and started for this element. */
  async whenReady() {
    this.io?.disconnect();
    const { WhenCard } = await load();
    if (this.isConnected && !this.controller) this.controller = new WhenCard(this);
    // A subscription's address module, if the basket has one, is part of what Check out waits for.
    await this.controller?.addressLoad;
    return this.controller;
  }
}

if (!customElements.get('lasura-basket-when')) {
  customElements.define('lasura-basket-when', LasuraBasketWhen);

  /* Check out, in the drawer and on the cart page (both name="checkout"), waits until the order
     has what it needs. Capture phase, so it runs before the form submits. If the card's working
     hasn't loaded yet, hold the press, load it, then press again if nothing is missing.
     Then a last check with the La Sura app (/apps/lasura/day): a day can fill while the basket is
     open. A full day still goes through (Matt, 2026-10-29, redesign L3): the basket marks the order
     _lasura_capacity = "over" and the app tags it call-customer for the kitchen to ring. A full
     collection time is still refused, so the shopper picks another time. If the app doesn't answer
     within 3s, checkout goes on (the app recounts the day when the order lands). */
  let passing = false;
  const pass = (button) => {
    if (!(button instanceof HTMLElement)) return;
    passing = true;
    button.click();
    passing = false;
  };
  const stillOpen = async (el, controller) => {
    const { iso: day, method, time } = controller.state || {};
    if (!day || el.dataset.dayCheck == null) {
      await controller.markCapacity?.();
      return true;
    }
    const slot = method === 'collect' && time ? `&t=${encodeURIComponent(time)}` : '';
    let answer;
    try {
      const response = await fetch(`${el.dataset.dayCheck}?d=${day}${slot}`, { signal: AbortSignal.timeout(3000) });
      if (response.ok) answer = await response.json();
    } catch {}
    if (!answer?.slotFull) {
      await controller.markCapacity?.(Boolean(answer?.full));
      return true;
    }
    let load = {};
    try {
      load = JSON.parse(el.dataset.slotLoad || '{}') || {};
    } catch {}
    (load[day] ||= {})[time] = Number(el.dataset.slotLimit) || 1;
    el.dataset.slotLoad = JSON.stringify(load);
    controller.flag(el.dataset.textJustFullTime || controller.missing());
    return false;
  };
  /* And checkout's Ship / Pickup tab: wait for the card's delivery preference to land (3s cap). */
  const checkThenPass = async (el, controller, button) => {
    const [open] = await Promise.all([stillOpen(el, controller), controller.settled?.()]);
    if (open) pass(button);
  };
  const guard = (event, button) => {
    if (passing) return;
    const cards = Array.from(document.querySelectorAll('lasura-basket-when'));
    const el = cards.find((c) => c.getClientRects().length > 0) || cards[0];
    if (!(el instanceof LasuraBasketWhen)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    /* Only a card that won't LOAD lets the click through: we already stopped it, and the La Sura
       app checks the order afterwards. An error while checking (the card's construction, missing(),
       flag(), or the last check with the app inside checkThenPass, which is returned so it is
       caught here) keeps Check out blocked and is logged. */
    load().then(
      () =>
        el.whenReady().then((controller) => {
          const message = controller?.missing();
          if (message) controller.flag(message);
          else if (controller) return checkThenPass(el, controller, button);
          else pass(button);
        }),
      () => pass(button)
    ).catch((error) => console.error('lasura-basket-when: Check out check failed', error));
  };
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element && event.target.closest('[name="checkout"]');
      if (button) guard(event, button);
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      if (event.submitter?.getAttribute('name') === 'checkout') guard(event, event.submitter);
    },
    true
  );
}
